import {
  useState,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useContext,
} from 'react';
import { debounce } from 'lodash';
import { AppContext } from '../components/FaqOoi/Widget/SettingsProvider/SettingsProvider';
import { PaginationState } from './usePaginationQuestions';

export interface PaginationSearchQuestionsProps {
  bottomRef: React.RefObject<HTMLDivElement>;
}

export const usePaginationSearchQuestions = ({
  bottomRef,
}: PaginationSearchQuestionsProps) => {
  const {
    selectedCategory,
    searchTerm,
    appAPI: { getSearchTerm, getListQuestions },
    searchTermResponse,
    listQuestionsResponse,
  } = useContext(AppContext);
  const [searchPaginationState, setSearchPaginationState] =
    useState<PaginationState>({
      cursor: null,
      loading: false,
      hasMore: false,
    });

  const firstLoad = useRef(true);

  useEffect(() => {
    if (!searchTerm) {
      return;
    }
    try {
      if (listQuestionsResponse && searchTermResponse) {
        setSearchPaginationState({
          cursor: searchTermResponse.pagingMetadata?.cursors?.next || null,
          loading: false,
          hasMore: searchTermResponse.pagingMetadata?.hasNext || false,
        });
      }
    } catch (error) {
      console.error('Error fetching questions:', error);
      setSearchPaginationState((prev) => ({ ...prev, loading: false }));
    }
  }, [listQuestionsResponse, searchTermResponse, searchTerm]);

  // Search - fetch questions from API to get search results
  const fetchQuestionsSearch = useCallback(
    async (term?: string, currentCategory?: typeof selectedCategory) => {
      setSearchPaginationState((prev) => ({ ...prev, loading: true }));
      try {
        if (term) {
          await getSearchTerm({ term });
        } else {
          await getListQuestions({
            categoryId: currentCategory,
          });
        }
      } catch (error) {
        console.error('Error fetching questions:', error);
        setSearchPaginationState((prev) => ({ ...prev, loading: false }));
      }
    },
    [getListQuestions, getSearchTerm],
  );

  // Debounced search
  const debouncedSearch = useMemo(
    () => debounce(fetchQuestionsSearch, 700),
    [fetchQuestionsSearch],
  );

  useEffect(() => {
    if (!firstLoad.current) {
      debouncedSearch(searchTerm, selectedCategory);
    }
    firstLoad.current = false;
  }, [searchTerm, debouncedSearch, selectedCategory]);

  // Load More Questions (Infinite Scrolling)
  const loadMoreQuestions = useCallback(async () => {
    if (
      !searchPaginationState.cursor ||
      searchPaginationState.loading ||
      !searchPaginationState.hasMore
    ) {
      return;
    }

    setSearchPaginationState((prev) => ({ ...prev, loading: true }));
    try {
      await getSearchTerm({
        term: searchTerm,
        cursor: searchPaginationState.cursor,
        prev: searchTermResponse?.questionEntries,
      });
    } catch (error) {
      console.error('Error loading more questions:', error);
      setSearchPaginationState((prev) => ({ ...prev, loading: false }));
    }
  }, [
    searchPaginationState.cursor,
    searchPaginationState.loading,
    searchPaginationState.hasMore,
    getSearchTerm,
    searchTerm,
    searchTermResponse?.questionEntries,
  ]);

  // Intersection Observer for Infinite Scroll
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        if (
          entries[0].isIntersecting &&
          searchPaginationState.hasMore &&
          !searchPaginationState.loading
        ) {
          loadMoreQuestions();
        }
      },
      { rootMargin: `${window.innerHeight * 0.6}px` },
    );

    if (bottomRef.current) {
      observer.observe(bottomRef.current);
    }
    return () => observer.disconnect();
  }, [
    searchPaginationState.hasMore,
    searchPaginationState.loading,
    loadMoreQuestions,
    bottomRef,
  ]);

  return { searchPaginationState };
};
